import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import UserService from '../../services/UserService';
import util from '../../utils/util';
import { message, Alert } from 'antd';

export default function Profile() {
    const location = useLocation();
    const history  = useHistory();
    const isForced = new URLSearchParams(location.search).get('force') === '1'
                  || localStorage.getItem('force_password_change') === '1';

    const [passData, setPassData]       = useState({});
    const [profileData, setProfileData] = useState({});
    const [passSubmitting, setPassSubmitting]       = useState(false);
    const [profileSubmitting, setProfileSubmitting] = useState(false);
    const [landingPage, setLandingPage]         = useState(util.getDefaultLandingPage());
    const [landingSubmitting, setLandingSubmitting] = useState(false);
    const nav = util.getNav();

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPassData(prev => ({ ...prev, [name]: value }));
    };
    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const changePassword = (e) => {
        e.preventDefault();
        const { current_password, new_password, confirm_password } = passData;
        if (!isForced && !current_password) {
            message.error('Current password is required'); return;
        }
        if (!new_password || new_password.length < 8) {
            message.error('Password must be at least 8 characters'); return;
        }
        if (new_password !== confirm_password) {
            message.error('Passwords do not match'); return;
        }
        const fd = new FormData(e.target);
        // When forced (first login), backend skips current_password check
        if (isForced) fd.append('forced_change', '1');
        setPassSubmitting(true);
        UserService.changePassword(fd)
            .then(() => {
                message.success('Password changed successfully');
                localStorage.removeItem('force_password_change');
                setPassData({});
                if (isForced) history.push('/welcome');
            })
            .catch(e => message.error(e.message || 'Failed to change password. Please try again.'))
            .finally(() => setPassSubmitting(false));
    };

    const updateProfile = (e) => {
        e.preventDefault();
        if (!profileData.fname?.trim()) {
            message.error('First name is required'); return;
        }
        const fd = new FormData(e.target);
        // When forced (first login), backend skips current_password check
        if (isForced) fd.append('forced_change', '1');
        setProfileSubmitting(true);
        UserService.updateProfile(fd)
            .then(() => message.success('Profile Updated'))
            .catch(e => message.error(e.message || 'Failed to update profile. Please try again.'))
            .finally(() => setProfileSubmitting(false));
    };

    const saveLandingPage = (e) => {
        e.preventDefault();
        setLandingSubmitting(true);
        UserService.setLandingPage(landingPage)
            .then(() => {
                message.success(landingPage ? 'Default landing page saved' : 'Preference cleared — back to default');
                // Keep the local cache in sync so it's correct even before next login
                window.localStorage['default_landing_page'] = landingPage || '';
            })
            .catch(e => message.error(e.message || 'Failed to save landing page'))
            .finally(() => setLandingSubmitting(false));
    };

    useEffect(() => {
        let isMounted = true;
        UserService.profileDetail().then(res => {
            if (!isMounted) return;
            const r = res.data.result;
            setProfileData({
                fname: r.fname, lname: r.lname, name: r.name,
                email: r.email, mobile: r.mobile,
            });
        }).catch(e => { if (isMounted) message.error(e.message || 'Failed to load profile'); });
        return () => { isMounted = false; };
    }, []);

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>{isForced ? 'Set New Password' : 'Profile'}</h2></div>
            </div>
            <div className="page-pad">
                {isForced && (
                    <Alert
                        type="warning"
                        showIcon
                        message="You must change your password before continuing"
                        description="This is required for first-time login. Choose a strong password you haven't used before."
                        style={{ marginBottom: 20, maxWidth: 500 }}
                    />
                )}
                <div className="card1 custom-card card-tabs mb15">
                    <div className="card-content">
                        {!isForced && (
                            <ul className="nav nav-pills uc">
                                <li className="nav-item">
                                    <a className="nav-link active" href="#pills2" data-toggle="pill">Profile</a>
                                </li>
                                <li className="nav-item">
                                    <a className="nav-link" href="#pills1" data-toggle="pill">Change Password</a>
                                </li>
                                <li className="nav-item">
                                    <a className="nav-link" href="#pills3" data-toggle="pill">Landing Page</a>
                                </li>
                            </ul>
                        )}
                        <div className="tab-content">
                            {/* Password tab */}
                            <div className={`tab-pane fade${isForced ? ' active show' : ''}`} id="pills1">
                                <div className="w500 mw-100">
                                    <form onSubmit={changePassword} autoComplete="off">
                                        <div className="row mingap">
                                            {!isForced && (
                                                <div className="form-group col-md-12">
                                                    <label className="req" htmlFor="current_password">Current Password</label>
                                                    <input type="password" id="current_password" className="form-control" name="current_password"
                                                        value={passData.current_password || ''} onChange={handlePasswordChange}
                                                        autoComplete="current-password" required disabled={passSubmitting} />
                                                </div>
                                            )}
                                            <div className="form-group col-md-12">
                                                <label className="req" htmlFor="new_password">New Password</label>
                                                <input type="password" id="new_password" className="form-control" name="new_password"
                                                    value={passData.new_password || ''} onChange={handlePasswordChange}
                                                    autoComplete="new-password" minLength={8} required disabled={passSubmitting} />
                                                <small className="text-muted">Minimum 8 characters</small>
                                            </div>
                                            <div className="form-group col-md-12">
                                                <label className="req" htmlFor="confirm_password">Confirm Password</label>
                                                <input type="password" id="confirm_password" className="form-control" name="confirm_password"
                                                    value={passData.confirm_password || ''} onChange={handlePasswordChange}
                                                    autoComplete="new-password" required disabled={passSubmitting} />
                                            </div>
                                            <div className="col-md-12">
                                                <button type="submit" className="btn btn-info" disabled={passSubmitting}>
                                                    {passSubmitting ? 'Saving…' : (isForced ? 'Set Password & Continue' : 'Change Password')}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* Profile tab */}
                            {!isForced && (
                                <div className="tab-pane fade active show" id="pills2">
                                    <div className="w500 mw-100">
                                        <form onSubmit={updateProfile} autoComplete="off">
                                            <div className="row mingap">
                                                <div className="form-group col-md-12">
                                                    <label className="req" htmlFor="fname">First Name</label>
                                                    <input type="text" id="fname" className="form-control" name="fname"
                                                        value={profileData.fname || ''} onChange={handleProfileChange}
                                                        required disabled={profileSubmitting} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label htmlFor="lname">Last Name</label>
                                                    <input type="text" id="lname" className="form-control" name="lname"
                                                        value={profileData.lname || ''} onChange={handleProfileChange}
                                                        disabled={profileSubmitting} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label htmlFor="email">Email</label>
                                                    <input type="email" id="email" className="form-control" name="email"
                                                        value={profileData.email || ''} onChange={handleProfileChange}
                                                        disabled={profileSubmitting} />
                                                </div>
                                                <div className="form-group col-md-12">
                                                    <label htmlFor="mobile">Mobile</label>
                                                    <input type="tel" id="mobile" className="form-control" name="mobile"
                                                        value={profileData.mobile || ''} onChange={handleProfileChange}
                                                        maxLength={10} inputMode="numeric" disabled={profileSubmitting} />
                                                </div>
                                                <div className="col-md-12">
                                                    <button type="submit" className="btn btn-info" disabled={profileSubmitting}>
                                                        {profileSubmitting ? 'Saving…' : 'Update'}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* Landing Page tab */}
                            {!isForced && (
                                <div className="tab-pane fade" id="pills3">
                                    <div className="w500 mw-100">
                                        <p className="text-muted" style={{ marginBottom: 16 }}>
                                            Choose which page you land on right after logging in. Only pages you
                                            currently have access to are listed here — if your access changes later
                                            and this page is no longer available to you, you'll land on the default
                                            welcome page instead.
                                        </p>
                                        <form onSubmit={saveLandingPage} autoComplete="off">
                                            <div className="row mingap">
                                                <div className="form-group col-md-12">
                                                    <label htmlFor="landing_page">Default Landing Page</label>
                                                    <select id="landing_page" className="form-control"
                                                        value={landingPage || ''}
                                                        onChange={e => setLandingPage(e.target.value)}
                                                        disabled={landingSubmitting}>
                                                        <option value="">Default (Welcome page)</option>
                                                        {nav.map(n => (
                                                            <option key={n.path} value={n.path}>
                                                                {n.group_name ? `${n.group_name} — ${n.name}` : n.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-12">
                                                    <button type="submit" className="btn btn-info" disabled={landingSubmitting}>
                                                        {landingSubmitting ? 'Saving…' : 'Save'}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
